import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Btn, Chip, Loading, Msg, Section, Select, TextInput, fmtDate, panelPost, useAdminGet } from '../kit';
import {
  FlowGraphData, FlowLive, FlowNode, FlowScenario, FlowTrace, ScenarioExample, ScenarioRealRun, TraceRequest, TraceResult,
  TraceStep, asList, fmtMs, num, statusStyle,
} from './flowTypes';
import {
  Layout, Timeline, computeLayout, frameAt, laneOrder, normalizePath, realTotalOf, resolvePathEdges,
  scenarioTimeline, traceTimeline,
} from './flowLayout';
import FlowGraph, { FLOW_CSS, FlowLegend, FlowMode, Selection } from './FlowGraph';
import FlowNodePanel, { NavItem } from './FlowNodePanel';

/**
 * Search flow — the live search pipeline as an interactive, animated graph.
 *
 * Reads search/flow_graph (admin_search_flow.py): every step of
 * search_v2.engine.orchestrate as a node with its live params, sub-steps and
 * a real example, the edges between them (fallbacks included) and the named
 * scenarios with the exact path each one takes. Picking a scenario animates
 * its path; clicking a step opens what it does, where each value comes from
 * and what it did in the example.
 *
 * "Test a live query" posts search/flow_trace, which runs the real pipeline
 * with trace=True (no search_query_logs row, no result-cache read/write
 * unless asked) and returns the path taken with every step's input, output
 * and real timing. The replay moves the pulse along that path with each
 * node's dwell proportional to the milliseconds it really took.
 */

// ---------------------------------------------------------------------------
// helpers
// ---------------------------------------------------------------------------
function useWidth(): [(el: HTMLDivElement | null) => void, number] {
  const [w, setW] = useState(0);
  const ro = useRef<ResizeObserver | null>(null);
  const ref = useCallback((el: HTMLDivElement | null) => {
    ro.current?.disconnect();
    ro.current = null;
    if (!el) return;
    setW(el.clientWidth);
    if (typeof ResizeObserver === 'undefined') return;
    ro.current = new ResizeObserver(entries => {
      for (const e of entries) setW(Math.round(e.contentRect.width));
    });
    ro.current.observe(el);
  }, []);
  useEffect(() => () => ro.current?.disconnect(), []);
  return [ref, w];
}

function sanitizeGraph(d: any): FlowGraphData | null {
  if (!d || typeof d !== 'object') return null;
  const seen = new Set<string>();
  const nodes: FlowNode[] = (Array.isArray(d.nodes) ? d.nodes : [])
    .filter((n: any) => n && n.id && !seen.has(String(n.id)) && seen.add(String(n.id)))
    .map((n: any) => ({
      ...n, id: String(n.id), label: String(n.label ?? n.id), lane: String(n.lane || 'respond'),
      order: num(n.order) ?? 0, kind: String(n.kind || 'step'),
      params: Array.isArray(n.params) ? n.params : [],
      substeps: Array.isArray(n.substeps) ? n.substeps.filter((s: any) => s && s.id).map((s: any) => ({
        ...s, id: String(s.id), label: String(s.label ?? s.id), params: Array.isArray(s.params) ? s.params : [],
      })) : [],
    }));
  const ids = new Set(nodes.map(n => n.id));
  const subIds = new Set(nodes.flatMap(n => (n.substeps || []).map(s => s.id)));
  const eSeen = new Set<string>();
  const edges = (Array.isArray(d.edges) ? d.edges : [])
    .filter((e: any) => e && (ids.has(String(e.from)) || subIds.has(String(e.from)))
      && (ids.has(String(e.to)) || subIds.has(String(e.to))))
    .map((e: any, i: number) => ({ ...e, id: String(e.id ?? `e${i}`), from: String(e.from), to: String(e.to) }))
    .filter((e: any) => !eSeen.has(e.id) && eSeen.add(e.id));
  const scenarios: FlowScenario[] = (Array.isArray(d.scenarios) ? d.scenarios : [])
    .filter((s: any) => s && s.id)
    .map((s: any) => ({ ...s, id: String(s.id), label: String(s.label ?? s.id),
                        path: Array.isArray(s.path) ? s.path.map(String) : [],
                        edges: Array.isArray(s.edges) ? s.edges.map(String) : [],
                        real_run: s.real_run && typeof s.real_run === 'object' && !Array.isArray(s.real_run) ? s.real_run : null }));
  return { nodes, edges, scenarios, live: d.live || null, generated_at: d.generated_at };
}

function normalizeSteps(steps: TraceStep[] | undefined, g: FlowGraphData, parentOf: Map<string, string>): TraceStep[] {
  const ids = new Set(g.nodes.map(n => n.id));
  return (Array.isArray(steps) ? steps : []).filter(s => s && s.node_id).map(s => {
    const nid = String(s.node_id);
    if (!s.substep_id && !ids.has(nid) && parentOf.has(nid)) {
      return { ...s, node_id: parentOf.get(nid)!, substep_id: nid };
    }
    return { ...s, node_id: nid, substep_id: s.substep_id ? String(s.substep_id) : null };
  });
}

const splitList = (s: string) => s.split(/[,;]/).map(x => x.trim()).filter(Boolean);

interface FormState {
  query: string; country: string; university: string; trl: number[];
  as: 'paid' | 'free' | 'anonymous'; deep: boolean; useCache: boolean; extra: Record<string, any>;
}
const EMPTY_FORM: FormState = { query: '', country: '', university: '', trl: [], as: 'paid', deep: false, useCache: false, extra: {} };

function formFromExample(ex: ScenarioExample | null | undefined, scenarioId: string): FormState {
  const f: FormState = { ...EMPTY_FORM, extra: {} };
  if (!ex) return f;
  f.query = String(ex.query || '');
  f.as = ex.as === 'free' || ex.as === 'anonymous' ? ex.as : 'paid';
  f.deep = ex.force_route === 'engine';
  f.useCache = !!ex.use_cache || /cache/i.test(scenarioId);
  Object.entries(ex.filters || {}).forEach(([k, v]) => {
    if (v === null || v === undefined || v === '' || (Array.isArray(v) && !v.length)) return;
    if (k === 'countries') f.country = asList(v).join(', ');
    else if (k === 'university_names') f.university = asList(v).join(', ');
    else if (k === 'trl_levels') f.trl = (Array.isArray(v) ? v : [v]).map(Number).filter(n => n >= 1 && n <= 9);
    else f.extra[k] = v;
  });
  return f;
}

function requestFromForm(f: FormState): TraceRequest {
  const filters: Record<string, any> = { ...f.extra };
  if (splitList(f.country).length) filters.countries = splitList(f.country);
  if (splitList(f.university).length) filters.university_names = splitList(f.university);
  if (f.trl.length) filters.trl_levels = [...f.trl].sort((a, b) => a - b);
  return { query: f.query.trim(), filters, force_route: f.deep ? 'engine' : null, as: f.as, use_cache: f.useCache };
}

// ---------------------------------------------------------------------------
// player: one clock for scenario animations and trace replays
// ---------------------------------------------------------------------------
function usePlayer(timeline: Timeline | null) {
  const [t, setT] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [speed, setSpeed] = useState(1);
  const tRef = useRef(0);
  const speedRef = useRef(1);
  speedRef.current = speed;
  const totalRef = useRef(0);
  totalRef.current = timeline?.total || 0;
  const reduced = useMemo(() => {
    try { return !!window.matchMedia?.('(prefers-reduced-motion: reduce)').matches; } catch { return false; }
  }, []);

  const restart = useCallback(() => { tRef.current = 0; setT(0); setPlaying(true); }, []);
  useEffect(() => {
    if (!timeline) { tRef.current = 0; setT(0); setPlaying(false); return; }
    if (reduced) { tRef.current = timeline.total; setT(timeline.total); setPlaying(false); return; }
    restart();
  }, [timeline, reduced, restart]);

  useEffect(() => {
    if (!playing) return undefined;
    let raf = 0;
    let last = performance.now();
    const tick = (now: number) => {
      const dt = Math.min(100, Math.max(0, now - last));
      last = now;
      tRef.current = Math.min(totalRef.current, tRef.current + dt * speedRef.current);
      setT(tRef.current);
      if (tRef.current >= totalRef.current) { setPlaying(false); return; }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [playing]);

  return {
    t, playing, speed, setSpeed, restart,
    total: timeline?.total || 0,
    toggle: () => { if (tRef.current >= totalRef.current) restart(); else setPlaying(p => !p); },
    skip: () => { tRef.current = totalRef.current; setT(totalRef.current); setPlaying(false); },
    seek: (v: number) => { tRef.current = Math.max(0, Math.min(totalRef.current, v)); setT(tRef.current); },
  };
}

// ---------------------------------------------------------------------------
// the tab
// ---------------------------------------------------------------------------
const SearchFlow: React.FC = () => {
  const [tick, setTick] = useState(0);
  // The first load may use the server's 60 s graph cache; Refresh / Retry /
  // Reload (tick > 0) ask for a fresh build.
  const g = useAdminGet<any>('search', 'flow_graph', tick ? { refresh: 1 } : {}, [tick]);
  const graph = useMemo(() => sanitizeGraph(g.data), [g.data]);

  const [mode, setMode] = useState<FlowMode>('overview');
  const [scenarioId, setScenarioId] = useState<string | null>(null);
  const [trace, setTrace] = useState<{ req: TraceRequest; data: FlowTrace; at: number } | null>(null);
  const [sel, setSel] = useState<Selection | null>(null);
  const [panelView, setPanelView] = useState<'run' | 'generic'>('generic');
  const [orientPref, setOrientPref] = useState<'auto' | 'lr' | 'tb'>('auto');
  const [zoom, setZoom] = useState<'fit' | number>('fit');

  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [running, setRunning] = useState(false);
  const [runErr, setRunErr] = useState('');
  const [runStart, setRunStart] = useState(0);
  const [now, setNow] = useState(0);
  const runSeq = useRef(0);

  const [outerRef, outerW] = useWidth();
  const [graphRef, graphW] = useWidth();
  const panelRef = useRef<HTMLDivElement | null>(null);
  const wide = outerW >= 1400;

  // --- derived: layout ------------------------------------------------------
  const parentOf = useMemo(() => {
    const m = new Map<string, string>();
    graph?.nodes.forEach(n => (n.substeps || []).forEach(s => { if (!m.has(s.id)) m.set(s.id, n.id); }));
    return m;
  }, [graph]);
  const layouts = useMemo(() => (graph && graph.nodes.length
    ? { lr: computeLayout(graph, 'lr'), tb: computeLayout(graph, 'tb') } : null), [graph]);
  const avail = Math.max(320, (graphW || 900) - 4);
  const orientation: 'lr' | 'tb' = useMemo(() => {
    if (orientPref !== 'auto' || !layouts) return orientPref === 'tb' ? 'tb' : 'lr';
    const fitLR = avail / layouts.lr.width; const fitTB = avail / layouts.tb.width;
    // Across reads best when it fits; otherwise whichever shrinks the text less.
    return fitLR >= 0.85 || fitLR >= fitTB ? 'lr' : 'tb';
  }, [orientPref, layouts, avail]);
  const layout: Layout | null = layouts ? layouts[orientation] : null;
  const scale = layout ? (zoom === 'fit' ? Math.max(0.55, Math.min(1.2, avail / layout.width)) : zoom) : 1;

  // --- derived: path + timeline ----------------------------------------------
  const scenario = useMemo(() => graph?.scenarios.find(s => s.id === scenarioId) || null, [graph, scenarioId]);
  const normSteps = useMemo(() => (trace && graph ? normalizeSteps(trace.data.steps, graph, parentOf) : []),
    [trace, graph, parentOf]);
  const { path, pathEdges, timeline } = useMemo(() => {
    const none = { path: [] as string[], pathEdges: new Set<string>(), timeline: null as Timeline | null };
    if (!graph) return none;
    const known = new Set(graph.edges.map(e => e.id));
    if (mode === 'scenario' && scenario) {
      const p = normalizePath(scenario.path, graph, parentOf);
      const e = resolvePathEdges(p, scenario.edges, graph, parentOf);
      const set = new Set<string>([...e.filter(Boolean) as string[], ...(scenario.edges || []).filter(x => known.has(x))]);
      return { path: p, pathEdges: set, timeline: scenarioTimeline(p, e) };
    }
    if (mode === 'trace' && trace) {
      const p = normalizePath(trace.data.path, graph, parentOf);
      const e = resolvePathEdges(p, trace.data.edges, graph, parentOf);
      const set = new Set<string>([...e.filter(Boolean) as string[], ...(trace.data.edges || []).filter(x => known.has(x))]);
      return { path: p, pathEdges: set, timeline: traceTimeline(p, e, { ...trace.data, steps: normSteps }) };
    }
    return none;
  }, [graph, mode, scenario, trace, normSteps, parentOf]);
  const pathNodes = useMemo(() => new Set(path), [path]);

  const player = usePlayer(timeline);
  const frame = useMemo(() => (layout ? frameAt(timeline, player.t, layout) : null), [timeline, player.t, layout]);

  const nodeStatus = useMemo(() => {
    const m = new Map<string, { status: string; ms: number | null; t: number | null }>();
    if (mode !== 'trace' || !timeline) return m;
    timeline.segs.forEach(s => {
      if (s.kind === 'node' && s.step) m.set(s.nodeId, { status: String(s.step.status), ms: num(s.step.ms), t: num(s.step.t_start_ms) });
    });
    return m;
  }, [mode, timeline]);
  const subSteps = useMemo(() => {
    const m = new Map<string, TraceStep>();
    if (mode !== 'trace') return m;
    normSteps.forEach(s => { if (s.substep_id) { const k = `${s.node_id}/${s.substep_id}`; if (!m.has(k)) m.set(k, s); } });
    return m;
  }, [mode, normSteps]);
  const nodeSteps = useMemo(() => {
    const m = new Map<string, TraceStep[]>();
    if (mode !== 'trace') return m;
    normSteps.forEach(s => { if (!s.substep_id) m.set(s.node_id, [...(m.get(s.node_id) || []), s]); });
    return m;
  }, [mode, normSteps]);

  // --- navigation (Prev / Next through the path, sub-steps included) ---------
  const nodeById = useMemo(() => new Map((graph?.nodes || []).map(n => [n.id, n])), [graph]);
  const allOrder = useMemo(() => {
    if (!graph) return [] as string[];
    const lanes = laneOrder(graph.nodes);
    return [...graph.nodes].sort((a, b) => lanes.indexOf(a.lane) - lanes.indexOf(b.lane) || (num(a.order) ?? 0) - (num(b.order) ?? 0))
      .map(n => n.id);
  }, [graph]);
  const expand = useCallback((ids: string[]): NavItem[] => {
    const out: NavItem[] = [];
    const seen = new Set<string>();
    ids.forEach(id => {
      const n = nodeById.get(id);
      if (!n || seen.has(id)) return;
      seen.add(id);
      out.push({ id, label: n.label });
      (n.substeps || []).forEach(s => out.push({ id, sub: s.id, label: `${n.label} › ${s.label}` }));
    });
    return out;
  }, [nodeById]);
  const nav = useMemo(() => {
    const blank = { prev: null, next: null, pos: 0, total: 0, label: '' };
    if (!sel || sel.kind !== 'node') return blank;
    const find = (list: NavItem[]) => list.findIndex(x => x.id === sel.id && (x.sub || '') === (sel.sub || ''));
    let list = path.length ? expand(path) : [];
    let where = mode === 'trace' ? 'this run' : 'this scenario';
    let i = find(list);
    if (i < 0) { list = expand(allOrder); i = find(list); where = 'all steps'; }
    if (i < 0) return blank;
    return { prev: list[i - 1] || null, next: list[i + 1] || null, pos: i + 1, total: list.length, label: where };
  }, [sel, path, expand, allOrder, mode]);

  const select = useCallback((s: Selection) => {
    setSel(s);
    if (s.kind === 'node') setPanelView(mode === 'trace' ? 'run' : 'generic');
  }, [mode]);

  const closePanel = useCallback(() => setSel(null), []);

  useEffect(() => {
    if (!sel || wide) return;
    const el = panelRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    if (r.top > window.innerHeight - 80 || r.bottom < 0) el.scrollIntoView({ block: 'start', behavior: 'smooth' });
  }, [sel, wide]);

  useEffect(() => {
    if (!sel) return undefined;
    const onKey = (e: KeyboardEvent) => {
      const tag = (e.target as HTMLElement)?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || e.metaKey || e.ctrlKey || e.altKey) return;
      if (e.key === 'Escape') setSel(null);
      if (e.key === 'ArrowRight' && nav.next) { e.preventDefault(); setSel({ kind: 'node', id: nav.next.id, sub: nav.next.sub }); }
      if (e.key === 'ArrowLeft' && nav.prev) { e.preventDefault(); setSel({ kind: 'node', id: nav.prev.id, sub: nav.prev.sub }); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [sel, nav]);

  // --- actions ------------------------------------------------------------------
  const pickScenario = (id: string) => {
    if (mode === 'scenario' && scenarioId === id) { player.restart(); return; }
    setScenarioId(id);
    setMode('scenario');
  };

  const run = async (req: TraceRequest) => {
    const mine = ++runSeq.current;
    setRunning(true); setRunErr(''); setRunStart(Date.now()); setNow(Date.now());
    const { data, error } = await panelPost('search', 'flow_trace', req as unknown as Record<string, any>);
    if (mine !== runSeq.current) return;
    setRunning(false);
    if (error || !data) { setRunErr(error || 'The trace came back empty.'); return; }
    const d = data as FlowTrace;
    setTrace({ req, data: { ...d, path: Array.isArray(d.path) ? d.path : [], edges: Array.isArray(d.edges) ? d.edges : [],
                             steps: Array.isArray(d.steps) ? d.steps : [], results: Array.isArray(d.results) ? d.results : [] },
               at: Date.now() });
    setMode('trace');
    setPanelView('run');
  };
  useEffect(() => {
    if (!running) return undefined;
    const iv = setInterval(() => setNow(Date.now()), 100);
    return () => clearInterval(iv);
  }, [running]);

  const runScenarioLive = (s: FlowScenario) => {
    const f = formFromExample(s.example, s.id);
    setForm(f);
    run(requestFromForm(f));
  };

  // --- render -------------------------------------------------------------------
  if (g.loading && !graph) return <Loading label="Building the live search flow…" />;
  if (g.error && !graph) {
    return (
      <Section title="Search flow" right={<Btn small onClick={() => setTick(t => t + 1)}>Retry</Btn>}>
        <Msg kind="error">Could not load the flow graph: {g.error}</Msg>
      </Section>
    );
  }
  if (!graph || !layout || !frame) {
    return (
      <Section title="Search flow" right={<Btn small onClick={() => setTick(t => t + 1)}>Reload</Btn>}>
        <div className="text-sm text-grayscale-1100 py-6 text-center">The server returned an empty flow graph.</div>
      </Section>
    );
  }

  const finished = !!timeline && player.t >= timeline.total;
  const activeLabel = frame.activeSeg
    ? (frame.activeSeg.kind === 'node'
      ? nodeById.get(frame.activeSeg.nodeId)?.label
      : `→ ${nodeById.get(frame.activeSeg.nodeId)?.label || ''}`)
    : finished ? 'done' : '';
  const panel = sel && (
    <FlowNodePanel graph={graph} sel={sel} onSelect={select} onClose={closePanel}
      nodeSteps={nodeSteps} subSteps={subSteps} hasTrace={mode === 'trace' && !!trace}
      view={panelView} setView={setPanelView} nav={nav} pathNodes={pathNodes} />
  );

  return (
    <div ref={outerRef} className="flex flex-col gap-4 min-w-0">
      <style>{FLOW_CSS}</style>

      <LiveStrip live={graph.live} generatedAt={graph.generated_at} loading={g.loading}
        onRefresh={() => setTick(t => t + 1)} />

      {/* ---- scenarios ------------------------------------------------------- */}
      <Section title="Scenarios — pick one to watch the path it takes"
        right={mode !== 'overview' ? <Btn small onClick={() => setMode('overview')}>Show whole graph</Btn> : undefined}>
        <div className="flex gap-2 flex-wrap">
          {graph.scenarios.map(s => {
            const on = mode === 'scenario' && scenarioId === s.id;
            const q = s.example?.query;
            return (
              <button key={s.id} type="button" onClick={() => pickScenario(s.id)}
                title={s.description || s.label}
                className={`text-left rounded-xl border px-3 py-1.5 max-w-[15rem] transition-colors ${on
                  ? 'bg-secondary-500 border-secondary-500 text-white'
                  : 'border-grayscale-400 bg-white hover:bg-grayscale-200 text-grayscale-1300'}`}>
                <div className="text-xs font-bold font-grotesk truncate">{s.label}</div>
                <div className={`text-[11px] truncate ${on ? 'text-white/80' : 'text-grayscale-1100'}`}>
                  {q ? `“${q}”` : filtersSummary(s.example?.filters) || '(no query)'}
                </div>
              </button>
            );
          })}
          {!graph.scenarios.length && <span className="text-sm text-grayscale-1100">No scenarios defined.</span>}
        </div>
        {mode === 'scenario' && scenario && (
          <ScenarioCard s={scenario} graph={graph} onReplay={player.restart} onRunLive={() => runScenarioLive(scenario)}
            running={running} hasTrace={!!trace} onBackToTrace={() => setMode('trace')} />
        )}
        {mode === 'overview' && (
          <div className="text-xs text-grayscale-1100">
            Pick a scenario to animate its path, click any step for its live parameters and an example, or run a real
            query below and watch it travel through the pipeline.
          </div>
        )}
      </Section>

      {/* ---- live test ------------------------------------------------------- */}
      <LiveTest form={form} setForm={setForm} running={running} elapsed={running ? now - runStart : 0}
        onRun={() => run(requestFromForm(form))} error={runErr}
        onClear={trace ? () => { setTrace(null); if (mode === 'trace') setMode('overview'); } : undefined} />

      {mode === 'trace' && trace && (
        <TraceHeader trace={trace} graph={graph} onScenario={id => { setScenarioId(id); setMode('scenario'); }} />
      )}

      {/* ---- graph + panel ------------------------------------------------------ */}
      <div className={wide && sel ? 'grid gap-4 items-start' : 'flex flex-col gap-4'}
        style={wide && sel ? { gridTemplateColumns: 'minmax(0, 1fr) 420px' } : undefined}>
        <div className="bg-white rounded-xl border border-grayscale-300 p-3 flex flex-col gap-2 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            {timeline ? (
              <PlayerBar player={player} finished={finished} activeLabel={activeLabel || ''}
                trace={mode === 'trace'} realNow={frame.realNow} realTotal={timeline.realTotal} />
            ) : (
              <span className="text-xs text-grayscale-1100 flex-1">Whole pipeline — {graph.nodes.length} steps, {graph.edges.length} connections.</span>
            )}
            <div className="flex items-center gap-1 ml-auto">
              <Seg value={orientPref} onChange={v => setOrientPref(v as any)}
                options={[['auto', 'Auto'], ['lr', '⇢ Across'], ['tb', '⇣ Down']]} title="Layout direction" />
              <Seg value={zoom === 'fit' ? 'fit' : ''} onChange={() => setZoom('fit')} options={[['fit', 'Fit']]} title="Fit to width" />
              <button type="button" className="w-7 h-7 rounded-full border border-grayscale-400 text-sm hover:bg-grayscale-200"
                title="Zoom out" onClick={() => setZoom(Math.max(0.4, Math.round((scale - 0.1) * 10) / 10))}>−</button>
              <button type="button" className="w-7 h-7 rounded-full border border-grayscale-400 text-sm hover:bg-grayscale-200"
                title="Zoom in" onClick={() => setZoom(Math.min(1.8, Math.round((scale + 0.1) * 10) / 10))}>+</button>
            </div>
          </div>
          <div ref={graphRef} className="overflow-auto rounded-lg border border-grayscale-200 relative"
            onClick={() => setSel(null)}>
            {running && (
              <div className="absolute top-2 left-2 z-10 rounded-full bg-secondary-500 text-white text-xs px-3 py-1 shadow">
                Running the real pipeline… {(Math.max(0, now - runStart) / 1000).toFixed(1)} s
              </div>
            )}
            <div style={{ width: layout.width * scale, margin: layout.width * scale < avail ? '0 auto' : undefined }}>
              <FlowGraph graph={graph} layout={layout} mode={mode} pathNodes={pathNodes} pathEdges={pathEdges}
                frame={frame} nodeStatus={nodeStatus} subSteps={subSteps} selected={sel} onSelect={select} scale={scale} />
            </div>
          </div>
          <FlowLegend showStatus={mode === 'trace'} />
        </div>
        {sel && (
          <div ref={panelRef}
            className={`bg-white rounded-xl border border-grayscale-300 p-4 min-w-0 ${wide ? 'sticky top-4 max-h-[calc(100vh-2rem)] overflow-y-auto' : ''}`}>
            {panel}
          </div>
        )}
      </div>

      {mode === 'trace' && trace && (
        <>
          <StepTimeline steps={normSteps} path={path} graph={graph} realTotal={timeline?.realTotal || realTotalOf(trace.data)}
            realNow={finished ? null : frame.realNow} onSelect={select} />
          {(finished || trace.data.results.length === 0) ? (
            <ResultsStrip results={trace.data.results} trace={trace.data} />
          ) : (
            <div className="text-xs text-grayscale-1100 text-center">
              Results appear when the replay reaches the end — <button type="button" className="underline" onClick={player.skip}>skip to end</button>.
            </div>
          )}
        </>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// pieces
// ---------------------------------------------------------------------------
const filtersSummary = (f: Record<string, any> | null | undefined): string => {
  const parts = Object.entries(f || {})
    .filter(([, v]) => v !== null && v !== undefined && v !== '' && !(Array.isArray(v) && !v.length))
    .map(([k, v]) => `${k.replace(/_/g, ' ')}: ${Array.isArray(v) ? v.join(', ') : typeof v === 'object' ? JSON.stringify(v) : v}`);
  return parts.join(' · ');
};

const Seg: React.FC<{ value: string; onChange: (v: string) => void; options: [string, string][]; title?: string }> =
  ({ value, onChange, options, title }) => (
  <div className="inline-flex rounded-full border border-grayscale-400 p-0.5 text-[11px]" title={title}>
    {options.map(([v, l]) => (
      <button key={v} type="button" onClick={() => onChange(v)}
        className={`px-2 py-0.5 rounded-full whitespace-nowrap ${value === v ? 'bg-primary-500 text-white font-bold' : 'text-grayscale-1200 hover:bg-grayscale-200'}`}>
        {l}
      </button>
    ))}
  </div>
);

const LiveStrip: React.FC<{ live?: FlowLive | null; generatedAt?: string; loading: boolean; onRefresh: () => void }> =
  ({ live, generatedAt, loading, onRefresh }) => {
  const [open, setOpen] = useState(false);
  const ts = live?.typesense;
  const settings = live?.settings || {};
  const pill = (label: string, value: React.ReactNode, tone: 'ok' | 'bad' | 'plain' = 'plain', title?: string) => (
    <div title={title} className={`rounded-xl border px-3 py-1.5 min-w-0 ${tone === 'bad'
      ? 'border-red-200 bg-red-50' : tone === 'ok' ? 'border-green-200 bg-green-50' : 'border-grayscale-300 bg-white'}`}>
      <div className="text-[10px] uppercase tracking-wide text-grayscale-1100">{label}</div>
      <div className="text-[13px] font-bold text-grayscale-1300 font-grotesk truncate max-w-[16rem]">{value}</div>
    </div>
  );
  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-stretch gap-2 flex-wrap">
        {pill('Engine', live?.engine || '—')}
        {pill('Simple queries go to', live?.simple_route || '—')}
        {pill('Typesense', ts ? (ts.up ? `up · ${num(ts.docs) !== null ? `${num(ts.docs)!.toLocaleString()} docs` : 'docs ?'}` : 'down') : '—',
          ts ? (ts.up ? 'ok' : 'bad') : 'plain',
          ts && !ts.up ? `${ts.error || 'unreachable'} — simple queries fall back to SQL. Typesense runs on the server only; a local backend always sees it down.` : undefined)}
        {pill('Pinecone', live?.pinecone ? `${live.pinecone.index || '—'}${live.pinecone.namespace ? ` · ${live.pinecone.namespace}` : ''}` : '—')}
        {pill('Rescore model', live?.rescore_model || '—', 'plain', live?.rescore_model)}
        {pill('Corpus', num(live?.corpus_count) !== null ? `${num(live?.corpus_count)!.toLocaleString()} techs` : '—')}
        <div className="flex flex-col justify-center gap-1 ml-auto">
          <div className="flex gap-1.5">
            <Btn small onClick={() => setOpen(o => !o)}>{open ? 'Hide settings' : `All settings (${Object.keys(settings).length})`}</Btn>
            <Btn small onClick={onRefresh} disabled={loading}>{loading ? 'Refreshing…' : 'Refresh'}</Btn>
          </div>
          {generatedAt && <div className="text-[10.5px] text-grayscale-1100 text-right">graph built {fmtDate(generatedAt)}</div>}
        </div>
      </div>
      {open && (
        <div className="flex flex-wrap gap-1.5 rounded-xl border border-grayscale-300 bg-white p-2.5">
          {Object.entries(settings).map(([k, v]) => (
            <span key={k} className="inline-flex items-center gap-1 rounded-full bg-grayscale-200 px-2 py-0.5 text-[11px] max-w-full [overflow-wrap:anywhere]">
              <span className="text-grayscale-1100 font-mono">{k}</span>
              <b className="text-grayscale-1300">{typeof v === 'boolean' ? (v ? 'on' : 'off') : typeof v === 'object' ? JSON.stringify(v) : String(v)}</b>
            </span>
          ))}
          {!Object.keys(settings).length && <span className="text-xs text-grayscale-1100">No settings reported.</span>}
        </div>
      )}
    </div>
  );
};

const ScenarioCard: React.FC<{
  s: FlowScenario; graph: FlowGraphData; onReplay: () => void; onRunLive: () => void; running: boolean;
  hasTrace: boolean; onBackToTrace: () => void;
}> = ({ s, graph, onReplay, onRunLive, running, hasTrace, onBackToTrace }) => {
  const ex = s.example || {};
  const labels = new Map(graph.nodes.map(n => [n.id, n.label]));
  const notes = asList(s.notes);
  return (
    <div className="rounded-xl border border-secondary-400 bg-secondary-100/40 p-3 flex flex-col gap-2 min-w-0 [overflow-wrap:anywhere]">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0 flex-1">
          <div className="text-sm font-bold text-grayscale-1300 font-grotesk">{s.label}</div>
          {s.description && <p className="text-[13px] text-grayscale-1200 mt-0.5 whitespace-pre-line">{s.description}</p>}
        </div>
        <div className="flex gap-1.5 flex-wrap">
          <Btn small onClick={onReplay}>⟲ Replay</Btn>
          <Btn small kind="primary" onClick={onRunLive} disabled={running}
            title="Run this scenario's example through the real pipeline and replay it">▶ Run it live</Btn>
          {hasTrace && <Btn small onClick={onBackToTrace}>Back to live run</Btn>}
        </div>
      </div>
      <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-grayscale-1200">
        <span><span className="text-grayscale-1100">query </span><b>{ex.query ? `“${ex.query}”` : '(empty)'}</b></span>
        {filtersSummary(ex.filters) && <span><span className="text-grayscale-1100">filters </span><b>{filtersSummary(ex.filters)}</b></span>}
        <span><span className="text-grayscale-1100">route </span><b>{ex.force_route ? `forced ${ex.force_route}` : 'classified'}</b></span>
        <span><span className="text-grayscale-1100">as </span><b>{ex.as || 'paid'}</b></span>
      </div>
      <div className="flex flex-wrap items-center gap-1 text-[11px]">
        {s.path.map((id, i) => (
          <React.Fragment key={`${id}-${i}`}>
            {i > 0 && <span className="text-grayscale-1100">→</span>}
            <span className="rounded-full bg-white border border-grayscale-300 px-2 py-px text-grayscale-1200">{labels.get(id) || id}</span>
          </React.Fragment>
        ))}
      </div>
      {notes.length > 0 && (
        <ul className="list-disc pl-5 text-[12px] text-grayscale-1200">{notes.map((n, i) => <li key={i}>{n}</li>)}</ul>
      )}
      {s.real_run && <RealRunBlock r={s.real_run} />}
    </div>
  );
};

/** The latest matching production run for a scenario (search_query_logs). */
const RealRunBlock: React.FC<{ r: ScenarioRealRun }> = ({ r }) => {
  const tm = r.timings || {};
  const main = Object.entries(tm)
    .filter(([k, v]) => k !== 'total_ms' && num(v) !== null && (num(v) as number) >= 1)
    .sort((a, b) => (num(b[1]) as number) - (num(a[1]) as number))
    .slice(0, 5);
  const detected = filtersSummary(r.ai_filters);
  const top = (r.top || []).slice(0, 3).map(t => t && t.title).filter(Boolean) as string[];
  return (
    <div className="rounded-lg border border-grayscale-300 bg-white px-2.5 py-2 flex flex-col gap-1 text-xs text-grayscale-1200">
      <div className="text-[11px] font-bold text-grayscale-1100">Last real run{r.date ? ` · ${r.date}` : ''}</div>
      <div className="flex flex-wrap gap-x-4 gap-y-0.5">
        <span><span className="text-grayscale-1100">query </span><b>{r.query ? `“${r.query}”` : '(empty)'}</b></span>
        {filtersSummary(r.filters) && <span><span className="text-grayscale-1100">filters </span><b>{filtersSummary(r.filters)}</b></span>}
        {detected && <span><span className="text-grayscale-1100">detected </span><b>{detected}</b></span>}
        {r.backend && <span><span className="text-grayscale-1100">backend </span><b>{r.backend}</b></span>}
        <span><span className="text-grayscale-1100">results </span><b>{num(r.total_results) !== null ? (num(r.total_results) as number).toLocaleString() : '—'}</b></span>
        <span><span className="text-grayscale-1100">total </span><b>{fmtMs(r.total_ms ?? tm.total_ms)}</b></span>
        {r.rescore?.model && <span><span className="text-grayscale-1100">rescore </span><b>{r.rescore.model}{num(r.rescore.scored) !== null ? ` · ${r.rescore.scored} scored` : ''}</b></span>}
      </div>
      {main.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {main.map(([k, v]) => (
            <span key={k} className="rounded-full bg-grayscale-200 px-2 py-px text-[11px]">
              {k.replace(/_ms$/, '').replace(/_/g, ' ')} <b className="text-grayscale-1300">{fmtMs(v)}</b>
            </span>
          ))}
        </div>
      )}
      {top.length > 0 && (
        <div className="text-[11px] text-grayscale-1100 truncate" title={top.join('\n')}>top: {top.join(' · ')}</div>
      )}
    </div>
  );
};

const LiveTest: React.FC<{
  form: FormState; setForm: React.Dispatch<React.SetStateAction<FormState>>; running: boolean; elapsed: number;
  onRun: () => void; error: string; onClear?: () => void;
}> = ({ form, setForm, running, elapsed, onRun, error, onClear }) => {
  const set = <K extends keyof FormState>(k: K) => (v: FormState[K]) => setForm(f => ({ ...f, [k]: v }));
  const noInput = !form.query.trim() && !Object.keys(requestFromForm(form).filters).length;
  return (
    <Section title="Test a live query"
      right={onClear ? <Btn small onClick={onClear}>Clear run</Btn> : undefined}>
      <form className="flex flex-col gap-2" onSubmit={e => { e.preventDefault(); if (!running && !noInput) onRun(); }}>
        <div className="flex gap-2 flex-wrap items-center">
          <TextInput value={form.query} onChange={set('query')} wide
            placeholder="Type a search, e.g. battery  ·  or leave empty and pick filters for a filter browse" />
          <button type="submit" disabled={running || noInput}
            className="py-1.5 px-4 text-sm rounded-full font-grotesk font-medium bg-primary-500 text-grayscale-100 disabled:bg-primary-100 disabled:text-primary-300">
            {running ? `Running… ${(elapsed / 1000).toFixed(1)} s` : '▶ Run through the flow'}
          </button>
        </div>
        <div className="flex gap-2 flex-wrap items-center text-xs">
          <TextInput value={form.country} onChange={set('country')} placeholder="Country (e.g. India)" />
          <TextInput value={form.university} onChange={set('university')} placeholder="University name" />
          <div className="flex items-center gap-0.5" title="TRL filter (multi-select)">
            <span className="text-grayscale-1100 mr-1">TRL</span>
            {[1, 2, 3, 4, 5, 6, 7, 8, 9].map(n => {
              const on = form.trl.includes(n);
              return (
                <button key={n} type="button"
                  onClick={() => set('trl')(on ? form.trl.filter(x => x !== n) : [...form.trl, n])}
                  className={`w-6 h-6 rounded-md text-[11px] border ${on ? 'bg-primary-500 border-primary-500 text-white font-bold' : 'border-grayscale-400 text-grayscale-1200 hover:bg-grayscale-200'}`}>
                  {n}
                </button>
              );
            })}
          </div>
          <span className="text-grayscale-1100 ml-1">Run as</span>
          <Select value={form.as} onChange={v => set('as')(v as FormState['as'])} options={['paid', 'free', 'anonymous']}
            labels={{ paid: 'Paid (no cap)', free: 'Free (signed-in Explorer)', anonymous: 'Anonymous visitor' }} />
          <label className="inline-flex items-center gap-1.5 cursor-pointer">
            <input type="checkbox" checked={form.deep} onChange={e => set('deep')(e.target.checked)} />
            Deep search <span className="text-grayscale-1100">(force engine)</span>
          </label>
          <label className="inline-flex items-center gap-1.5 cursor-pointer" title="Off: the run neither reads nor fills the 5-minute result cache">
            <input type="checkbox" checked={form.useCache} onChange={e => set('useCache')(e.target.checked)} />
            Use result cache
          </label>
        </div>
        {Object.keys(form.extra).length > 0 && (
          <div className="flex gap-1.5 flex-wrap">
            {Object.entries(form.extra).map(([k, v]) => (
              <Chip key={k} label={k.replace(/_/g, ' ')} value={Array.isArray(v) ? v.join(', ') : typeof v === 'object' ? JSON.stringify(v) : String(v)}
                onClear={() => setForm(f => { const extra = { ...f.extra }; delete extra[k]; return { ...f, extra }; })} />
            ))}
          </div>
        )}
        <div className="text-[11px] text-grayscale-1100">
          Runs the real pipeline — real LLM, Pinecone and database calls — with tracing on: no search log row is written
          and the result cache is neither read nor filled unless "Use result cache" is ticked. Complex queries take 10–25 s.
        </div>
        {error && <Msg kind="error">{error}</Msg>}
      </form>
    </Section>
  );
};

const PlayerBar: React.FC<{
  player: ReturnType<typeof usePlayer>; finished: boolean; activeLabel: string; trace: boolean;
  realNow: number; realTotal: number;
}> = ({ player, finished, activeLabel, trace, realNow, realTotal }) => {
  const pct = player.total ? (player.t / player.total) * 100 : 0;
  return (
    <div className="flex items-center gap-2 flex-wrap flex-1 min-w-[18rem]">
      <button type="button" onClick={player.restart} title="Replay from the start"
        className="h-7 px-2.5 rounded-full border border-grayscale-400 text-xs hover:bg-grayscale-200">⟲ Replay</button>
      <button type="button" onClick={player.toggle} title={player.playing ? 'Pause' : 'Play'}
        className="h-7 w-9 rounded-full bg-secondary-500 text-white text-xs">{player.playing ? '❚❚' : '▶'}</button>
      <button type="button" onClick={player.skip} disabled={finished} title="Skip to end"
        className="h-7 px-2.5 rounded-full border border-grayscale-400 text-xs hover:bg-grayscale-200 disabled:opacity-40">⏭ Skip to end</button>
      <label className="flex items-center gap-1 text-[11px] text-grayscale-1100" title="Replay speed">
        speed
        <input type="range" min={0.25} max={3} step={0.25} value={player.speed}
          onChange={e => player.setSpeed(Number(e.target.value))} className="w-20 accent-[#2C5695]" />
        <span className="w-8 text-grayscale-1300 font-medium">{player.speed}×</span>
      </label>
      <div className="flex-1 min-w-[8rem] flex flex-col gap-0.5">
        <div className="h-1.5 rounded-full bg-grayscale-200 overflow-hidden cursor-pointer"
          onClick={e => {
            const r = (e.currentTarget as HTMLDivElement).getBoundingClientRect();
            player.seek(((e.clientX - r.left) / r.width) * player.total);
          }}>
          <div className="h-full bg-secondary-500" style={{ width: `${pct}%` }} />
        </div>
        <div className="flex justify-between text-[10.5px] text-grayscale-1100">
          <span className="truncate">{activeLabel}</span>
          {trace && <span>t = {fmtMs(realNow)} of {fmtMs(realTotal)} real</span>}
        </div>
      </div>
    </div>
  );
};

const TraceHeader: React.FC<{
  trace: { req: TraceRequest; data: FlowTrace; at: number }; graph: FlowGraphData; onScenario: (id: string) => void;
}> = ({ trace, graph, onScenario }) => {
  const { req, data } = trace;
  const guess = data.scenario_guess ? graph.scenarios.find(s => s.id === data.scenario_guess) : null;
  const degraded = asList(data.degraded);
  const total = num(data.totals?.total); const unc = num(data.totals?.total_uncapped);
  return (
    <div className="rounded-xl border border-secondary-400 bg-white p-3 flex flex-col gap-2 min-w-0 [overflow-wrap:anywhere]">
      <div className="flex items-center gap-x-4 gap-y-1 flex-wrap text-sm">
        <span className="font-bold text-grayscale-1300 font-grotesk">Live run</span>
        <span className="text-grayscale-1300">{req.query ? `“${req.query}”` : '(no query — filter browse)'}</span>
        {filtersSummary(req.filters) && <span className="text-xs text-grayscale-1200">{filtersSummary(req.filters)}</span>}
        <span className="text-xs text-grayscale-1100">as <b className="text-grayscale-1300">{req.as}</b>{req.force_route ? ' · deep search' : ''}{req.use_cache ? ' · cache allowed' : ''}</span>
      </div>
      <div className="flex items-center gap-2 flex-wrap text-xs">
        <Stat label="route" value={data.route || '—'} />
        <Stat label="engine" value={data.engine || '—'} />
        <Stat label="took" value={fmtMs(realTotalOf(data))} />
        {num(data.took_ms) !== null && (
          <span className="text-grayscale-1100" title="Wall time of the whole trace call: the search, plus the admin wrapper and (on a quick route) the wait after the response for the background expansion">
            trace wall time {fmtMs(data.took_ms)}
          </span>
        )}
        <Stat label="results" value={total !== null ? `${total.toLocaleString()}${unc !== null && unc !== total ? ` of ${unc.toLocaleString()}` : ''}` : '—'} />
        <Stat label="steps" value={`${asList(data.path).length} on path · ${(data.steps || []).length} recorded`} />
        {guess && (
          <button type="button" onClick={() => onScenario(guess.id)}
            className="rounded-full border border-secondary-400 text-secondary-500 px-2.5 py-0.5 hover:bg-secondary-100"
            title="Show this scenario's documented path">
            looks like: {guess.label} →
          </button>
        )}
      </div>
      {(data.success === false || data.error) && (
        <Msg kind="error">The pipeline answered with an error: {String(data.error || data.message || 'unknown')}</Msg>
      )}
      {data.message && !data.error && <div className="text-xs text-grayscale-1200">{String(data.message)}</div>}
      {entNote(data.entitlements) && (
        <div className="text-xs text-grayscale-1200"><span className="text-grayscale-1100">entitlement simulated: </span>{entNote(data.entitlements)}</div>
      )}
      {asList(data.notes).length > 0 && (
        <ul className="list-disc pl-5 text-xs text-grayscale-1200">{asList(data.notes).map((n, i) => <li key={i}>{n}</li>)}</ul>
      )}
      {degraded.length > 0 && (
        <div className="rounded-lg border border-amber-300 bg-amber-50 px-2.5 py-1.5 text-xs text-amber-900">
          <b>Degraded:</b>
          <ul className="list-disc pl-5">{degraded.map((d, i) => <li key={i}>{d}</li>)}</ul>
        </div>
      )}
    </div>
  );
};

/** One line for the entitlement the trace ran under, whatever shape the server sends. */
const entNote = (e: any): string => {
  if (!e) return '';
  if (typeof e === 'string') return e;
  if (typeof e !== 'object') return String(e);
  const parts: string[] = [];
  if (e.plan) parts.push(String(e.plan));
  else if (e.as) parts.push(`as ${e.as}`);
  if ('cap' in e) parts.push(e.cap === null || e.cap === undefined ? 'no cap' : `capped at ${e.cap}`);
  if (num(e.preview_results) !== null) parts.push(`preview of ${num(e.preview_results)} results`);
  if (e.can_search === false) parts.push('search locked');
  if (e.can_filter === false || e.filters_locked || e.filters_dropped) parts.push('filters locked');
  if (e.lock_enabled === false) parts.push('free-tier lock off');
  if (e.lock_note || e.note) parts.push(String(e.lock_note || e.note));
  return parts.length ? parts.join(' · ') : JSON.stringify(e).slice(0, 200);
};

const Stat: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
  <span className="rounded-full bg-grayscale-200 px-2.5 py-0.5">
    <span className="text-grayscale-1100">{label} </span><b className="text-grayscale-1300">{value}</b>
  </span>
);

const StepTimeline: React.FC<{
  steps: TraceStep[]; path: string[]; graph: FlowGraphData; realTotal: number; realNow: number | null;
  onSelect: (s: Selection) => void;
}> = ({ steps, path, graph, realTotal, realNow, onSelect }) => {
  const [open, setOpen] = useState(true);
  const byId = new Map(graph.nodes.map(n => [n.id, n]));
  const rows = useMemo(() => {
    const order = new Map<string, number>();
    path.forEach((id, i) => { if (!order.has(id)) order.set(id, i); });
    const lanes = laneOrder(graph.nodes);
    const rank = (s: TraceStep) => order.has(s.node_id) ? order.get(s.node_id)!
      : 1000 + lanes.indexOf(byId.get(s.node_id)?.lane || '') * 50 + (num(byId.get(s.node_id)?.order) ?? 0);
    return [...steps].map((s, i) => ({ s, i })).sort((a, b) =>
      rank(a.s) - rank(b.s) || Number(!!a.s.substep_id) - Number(!!b.s.substep_id)
      || (num(a.s.t_start_ms) ?? 0) - (num(b.s.t_start_ms) ?? 0) || a.i - b.i).map(x => x.s);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [steps, path, graph]);
  // The search's own duration, widened to fit any step that ended later (a
  // background expansion the trace waited for after the response).
  const span = Math.max(1, realTotal || 0, ...steps.map(s => (num(s.t_start_ms) ?? 0) + (num(s.ms) ?? 0)));
  const pct = (v: number) => `${Math.max(0, Math.min(100, (v / span) * 100))}%`;
  return (
    <Section title={`Step timeline — ${steps.length} recorded steps over ${fmtMs(span)}`}
      right={<Btn small onClick={() => setOpen(o => !o)}>{open ? 'Hide' : 'Show'}</Btn>}>
      {open && (
        <div className="flex flex-col gap-0.5 overflow-x-auto">
          <div className="flex items-center gap-2 text-[10px] text-grayscale-1100 min-w-[36rem]">
            <div className="w-56 shrink-0" />
            <div className="flex-1 relative h-3">
              {[0, 0.25, 0.5, 0.75, 1].map(f => (
                <span key={f} className={`absolute whitespace-nowrap ${f === 0 ? '' : f === 1 ? '-translate-x-full' : '-translate-x-1/2'}`}
                  style={{ left: `${f * 100}%` }}>{fmtMs(span * f)}</span>
              ))}
            </div>
            <div className="w-24 shrink-0" />
          </div>
          {rows.map((s, i) => {
            const n = byId.get(s.node_id);
            const sub = s.substep_id ? n?.substeps?.find(x => x.id === s.substep_id) : null;
            const t0 = num(s.t_start_ms); const ms = num(s.ms) ?? 0;
            const st = statusStyle(s.status);
            return (
              <button key={i} type="button"
                onClick={() => onSelect({ kind: 'node', id: s.node_id, sub: s.substep_id || undefined })}
                className="flex items-center gap-2 text-left rounded hover:bg-grayscale-200 px-1 min-w-[36rem]">
                <div className={`w-56 shrink-0 truncate text-[12px] ${sub || s.substep_id ? 'pl-4 text-grayscale-1200' : 'font-medium text-grayscale-1300'}`}>
                  {sub ? `↳ ${sub.label}` : s.substep_id ? `↳ ${s.substep_id}` : (n?.label || s.node_id)}
                </div>
                <div className="flex-1 relative h-3.5 bg-grayscale-200 rounded">
                  {t0 !== null && (
                    <div className="absolute top-0 bottom-0 rounded"
                      style={{ left: pct(t0), width: `max(3px, ${pct(ms)})`, background: st.stroke,
                               opacity: s.status === 'skipped' ? 0.45 : 0.9 }} />
                  )}
                  {realNow !== null && (
                    <div className="absolute top-[-2px] bottom-[-2px] w-px bg-secondary-500" style={{ left: pct(realNow) }} />
                  )}
                </div>
                <div className="w-24 shrink-0 flex items-center justify-end gap-1.5">
                  <span className="inline-block w-2 h-2 rounded-full" style={{ background: st.stroke }} title={String(s.status)} />
                  <span className="text-[11.5px] text-grayscale-1200">{s.status === 'skipped' && !ms ? 'skipped' : fmtMs(ms)}</span>
                </div>
              </button>
            );
          })}
          {!rows.length && <div className="text-sm text-grayscale-1100 py-2">The run recorded no steps.</div>}
        </div>
      )}
    </Section>
  );
};

const legsOf = (v: TraceResult['legs']): string[] => {
  if (!v) return [];
  if (Array.isArray(v)) return v.map(x => (typeof x === 'string' ? x : JSON.stringify(x)));
  if (typeof v === 'object') {
    return Object.entries(v).map(([k, x]) => (x === true || x === null || x === undefined ? k
      : typeof x === 'number' ? `${k} #${x}` : `${k} ${typeof x === 'object' ? JSON.stringify(x) : x}`));
  }
  return [String(v)];
};

const pctTone = (p: number | null) => (p === null ? 'bg-grayscale-200 text-grayscale-1100'
  : p >= 80 ? 'bg-green-100 text-green-800' : p >= 60 ? 'bg-emerald-50 text-emerald-700'
    : p >= 40 ? 'bg-amber-50 text-amber-800' : 'bg-grayscale-200 text-grayscale-1200');

const ResultsStrip: React.FC<{ results: TraceResult[]; trace: FlowTrace }> = ({ results, trace }) => {
  const total = num(trace.totals?.total); const unc = num(trace.totals?.total_uncapped);
  return (
    <Section title={`Results — top ${results.length}${total !== null ? ` of ${total.toLocaleString()}` : ''}${unc !== null && unc !== total ? ` (${unc.toLocaleString()} before the cap)` : ''}`}
      right={<span className="text-xs text-grayscale-1100">route <b>{trace.route || '—'}</b> · engine <b>{trace.engine || '—'}</b> · {fmtMs(realTotalOf(trace))}</span>}>
      {!results.length ? (
        <div className="text-sm text-grayscale-1100 py-3 text-center">This run returned no results.</div>
      ) : (
        <div className="flex gap-2.5 overflow-x-auto pb-2">
          {results.map((r, i) => {
            const p = num(r.percentage);
            const legs = legsOf(r.legs);
            const tip = [r.llm_reason ? `AI: ${r.llm_reason}` : '', r.score_rule ? `Score: ${r.score_rule}` : ''].filter(Boolean).join('\n\n');
            return (
              <div key={i} title={tip || undefined}
                className="group relative w-60 shrink-0 rounded-xl border border-grayscale-300 bg-white p-2.5 flex flex-col gap-1.5">
                <div className="flex items-center justify-between gap-2">
                  <span className="text-[11px] font-bold text-grayscale-1100">
                    #{r.rank ?? i + 1}
                    {r.shown === false && <span className="ml-1.5 font-normal rounded-full bg-grayscale-200 px-1.5" title="Served, but not on page 1: the trace shows the first page of 10; this row is on a later page">page 2+</span>}
                    {r.llm_band && <span className="ml-1.5 font-normal text-grayscale-1100">{r.llm_band}</span>}
                  </span>
                  <span className={`rounded-full px-2 py-px text-[11px] font-bold ${pctTone(p)}`}>{p !== null ? `${Math.round(p)}%` : '—'}</span>
                </div>
                <div className="text-[12.5px] font-medium text-grayscale-1300 leading-snug line-clamp-2">{r.title || '(untitled)'}</div>
                <div className="text-[11px] text-grayscale-1100 truncate">
                  {[r.university, r.country, r.trl !== null && r.trl !== undefined && r.trl !== '' ? `TRL ${r.trl}` : ''].filter(Boolean).join(' · ') || '—'}
                </div>
                {legs.length > 0 && (
                  <div className="flex gap-1 flex-wrap">
                    {legs.map(l => <span key={l} className="rounded-full bg-secondary-100 text-secondary-500 px-1.5 py-px text-[10px]">{l}</span>)}
                  </div>
                )}
                {r.llm_reason && (
                  <>
                    <div className="text-[10.5px] text-primary-700">AI reason — hover</div>
                    <div className="hidden group-hover:block absolute inset-0 rounded-xl bg-white/95 border border-primary-300 p-2.5 overflow-auto text-[11.5px] text-grayscale-1300 leading-snug">
                      <div className="font-bold text-[11px] text-grayscale-1100 mb-1">Why {p !== null ? `${Math.round(p)}%` : ''}</div>
                      {r.llm_reason}
                      {r.score_rule && <div className="mt-1.5 text-[10.5px] text-grayscale-1100">{r.score_rule}</div>}
                    </div>
                  </>
                )}
                {!r.llm_reason && r.score_rule && <div className="text-[10.5px] text-grayscale-1100 truncate">{r.score_rule}</div>}
              </div>
            );
          })}
        </div>
      )}
    </Section>
  );
};

export default SearchFlow;
